import { DropdownDivider, DropdownItem, DropdownSubmenu } from 'platform/components';
import { Icon } from 'platform/foundation-tw';
import { useColumnMenuContext } from '../../../../../../../../context/ColumnMenuContext';
import { useIsMobileView } from '../../../../../../../../hooks/useIsMobileView';
type ColumnMenuOptionsTabProps = {
  currentPinned: 'LEFT' | 'RIGHT' | null | undefined;
  /** Key of the column this menu belongs to — used to call onAutosizeColumn / onPinColumn */
  columnKey: string;
  /** Whether the column supports sorting — controls Sort submenu visibility */
  columnSortable: boolean;
  /** Called after any action to close the parent popover */
  onClose: () => void;
};
const PIN_OPTIONS: {
  label: string;
  value: 'LEFT' | 'RIGHT' | null;
}[] = [{
  label: 'No pin',
  value: null
}, {
  label: 'Pin left',
  value: 'LEFT'
}, {
  label: 'Pin right',
  value: 'RIGHT'
}];
const SORT_OPTIONS: {
  label: string;
  value: 'ASC' | 'DESC' | null;
}[] = [{
  label: 'Sort ascending',
  value: 'ASC'
}, {
  label: 'Sort descending',
  value: 'DESC'
}, {
  label: 'Clear sort',
  value: null
}];

/**
 * Options tab content for the column header menu.
 *
 * Renders: Sort submenu (if sortable), Pin column submenu (No pin / Pin left / Pin right),
 * Autosize this column, Autosize all columns, divider, Reset columns, Reset filters.
 *
 * Visibility of pinning and autosize groups is controlled via ColumnMenuContext
 * (showPinning / showAutosize flags from the backend definition).
 * All action callbacks are also read from ColumnMenuContext.
 *
 * Must be rendered inside a <Dropdown> / <DropdownContainer> so that DropdownItem
 * receives the required MenuContext + FloatingList context from platform/components.
 */
export function ColumnMenuOptionsTab(props: ColumnMenuOptionsTabProps) {
  const columnMenu = useColumnMenuContext();
  const isMobile = useIsMobileView();
  const normalizedPinned = props.currentPinned ?? null;
  const currentSort = columnMenu.columnStates.get(props.columnKey)?.sort ?? null;
  const handleSort = (sort: 'ASC' | 'DESC' | null) => {
    columnMenu.onSort(props.columnKey, sort);
    props.onClose();
  };
  const handlePin = (value: 'LEFT' | 'RIGHT' | null) => {
    columnMenu.onPinColumn(props.columnKey, value);
    props.onClose();
  };
  const handleAutosizeThis = () => {
    columnMenu.onAutosizeColumn(props.columnKey);
    props.onClose();
  };
  const handleAutosizeAll = () => {
    columnMenu.onAutosizeAllColumns();
    props.onClose();
  };
  const handleResetColumns = () => {
    columnMenu.onResetColumns();
    props.onClose();
  };
  const handleResetFilters = () => {
    columnMenu.onResetFilters();
    props.onClose();
  };
  return <div className="p-2">
      {props.columnSortable && (isMobile ?
    // Mobile: flat DropdownItems — DropdownSubmenu creates an isolated FloatingTree
    // that breaks useDismiss on touch (pointerdown fires before click reaches the item).
    <>
            {SORT_OPTIONS.map(option => <DropdownItem key={option.value ?? 'none'} label={option.label} isActive={currentSort === option.value && option.value !== null} isDisabled={option.value === null && currentSort === null} onClick={() => handleSort(option.value)} data-testid={`columnMenuSortOption-${option.value ?? 'none'}`} />)}
            <DropdownDivider />
          </> : <DropdownSubmenu label="Sort" leftIcon="content/sort" isActive={currentSort !== null} suffix={<Icon value="navigation/arrow_right" />} placement="right-start" strategy="fixed" data-testid="columnMenuSortSubmenu">
            {SORT_OPTIONS.map(option => <DropdownItem key={option.value ?? 'none'} label={option.label} isActive={currentSort === option.value && option.value !== null} isDisabled={option.value === null && currentSort === null} onClick={() => handleSort(option.value)} data-testid={`columnMenuSortOption-${option.value ?? 'none'}`} />)}
          </DropdownSubmenu>)}

      {columnMenu.showPinning && (isMobile ?
    // Mobile: flat DropdownItems for the same reason as Sort above
    <>
            {PIN_OPTIONS.map(option => <DropdownItem key={option.value ?? 'none'} label={option.label} isActive={normalizedPinned === option.value} onClick={() => handlePin(option.value)} data-testid={`columnMenuPinOption-${option.value ?? 'none'}`} />)}
            <DropdownDivider />
          </> : <DropdownSubmenu label="Pin column" leftIcon="custom/push_pin" isActive={normalizedPinned !== null} suffix={<Icon value="navigation/arrow_right" />} placement="right-start" strategy="fixed" data-testid="columnMenuPinSubmenu">
            {PIN_OPTIONS.map(option => <DropdownItem key={option.value ?? 'none'} label={option.label} isActive={normalizedPinned === option.value} onClick={() => handlePin(option.value)} data-testid={`columnMenuPinOption-${option.value ?? 'none'}`} />)}
          </DropdownSubmenu>)}

      {columnMenu.showAutosize && <>
          <DropdownItem label="Autosize this column" leftIcon="action/aspect_ratio" onClick={handleAutosizeThis} data-testid="columnMenuAutosizeThis" />
          <DropdownItem label="Autosize all columns" leftIcon="action/aspect_ratio" onClick={handleAutosizeAll} data-testid="columnMenuAutosizeAll" />
        </>}

      <DropdownDivider data-testid="columnMenuDivider" />

      <DropdownItem label="Reset columns" leftIcon="action/restore" onClick={handleResetColumns} data-testid="columnMenuResetColumns" />
      <DropdownItem label="Reset filters" leftIcon="content/filter_list" onClick={handleResetFilters} data-testid="columnMenuResetFilters" />
    </div>;
}